import React, { useMemo, useState } from "react";
import { Images } from "../../../utils/imageConstants";
import BreadCrumb from "../../../components/BreadCrumb";
import { useLocation, useNavigate } from "react-router-dom";
import { setActiveTab } from "../../../redux/slice/activeTabSlice";
import { useDispatch } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ITeams } from "./types/teams";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import { Api } from "../../../services/interceptor";
import { useAppSelector } from "../../../redux/hooks";
import { selectSelectedLeague } from "../../../redux/slice/selectedLeagueSlice";
import { canManageDivisionsInLeague } from "../../../utils/leagueAccess";
import { fetchDivisions } from "../../myTeam/api/myTeamApi";
import type { IDivisionResponse } from "../../myTeam/types/myTeam";
import SetDivisionsGroupModal from "./SetDivisionsGroupModal";
import UpdateTeamModal from "./teamInfo/UpdateTeamModal";
import toast from "react-hot-toast";
import { extractDivisionListFromPayload, extractTeamsFromGetAllResponse, getTeamsFromDivisionNode, normalizeDivisionList } from "./divisionTeamUtils";
import FormDropdown from "../../../components/common/FormDropdown";
import { resolvePublicImageUrl } from "../../../helper/helper";
import type { FormDropdownOption } from "../../../components/common/FormDropdown";

const flattenDivisionTeams = (divisions: IDivisionResponse[]): ITeams[] => {
  const seen = new Set<string>();
  const teams: ITeams[] = [];

  const visit = (division: IDivisionResponse) => {
    const divisionTeams = getTeamsFromDivisionNode(division);

    divisionTeams.forEach((team) => {
      const entry = team as {
        teamSnapshotId?: string;
        teamSnapShotId?: string;
        snapshotId?: string;
        teamId?: string;
        teamName?: string;
        teamSnapshotName?: string;
        teamCode?: string | null;
        imageUri?: string | null;
        imageUrl?: string | null;
        teamLogoUrl?: string | null;
        team?: {
          teamId?: string;
          teamName?: string;
          teamCode?: string | null;
          winMatchesCount?: number;
          lossMatchesCount?: number;
          drawMatchesCount?: number;
          imageUri?: string | null;
          imageUrl?: string | null;
        };
      };

      const snap = (entry.teamSnapshotId ?? entry.teamSnapShotId ?? entry.snapshotId ?? "").trim();
      const teamId = (entry.team?.teamId ?? entry.teamId ?? snap).trim();
      const teamName = entry.team?.teamName ?? entry.teamName ?? (entry.teamSnapshotName != null ? String(entry.teamSnapshotName) : "") ?? "";
      const teamCode = (entry.teamCode ?? entry.team?.teamCode ?? "").trim() || undefined;
      if (!teamName) return;
      const dedupeKey = snap || teamId;
      if (!dedupeKey || seen.has(dedupeKey)) return;

      seen.add(dedupeKey);
      // Carry every image-field variant the backend might use through to the
      // mapped object so the JSX can resolve a logo regardless of endpoint.
      const imageUrl =
        entry.imageUrl ??
        entry.team?.imageUrl ??
        entry.imageUri ??
        entry.team?.imageUri ??
        entry.teamLogoUrl ??
        null;
      teams.push({
        teamSnapshotId: snap,
        team: {
          teamId: teamId || snap,
          teamName,
          winMatchesCount: entry.team?.winMatchesCount ?? 0,
          lossMatchesCount: entry.team?.lossMatchesCount ?? 0,
          drawMatchesCount: entry.team?.drawMatchesCount ?? 0,
          imageUrl,
        },
        imageUrl,
        teamLogoUrl: entry.teamLogoUrl ?? null,
        ...(teamCode ? { teamCode } : {}),
      });
    });

    (division.childDivisions ?? []).forEach(visit);
  };

  divisions.forEach(visit);
  return teams;
};

const Teams: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isTournamentDetailsRoute = location.pathname.includes("/tournaments/tournaments-details") || location.pathname.includes("/tournamentDetails/") || /\/my-leagues\/[^/]+\/tournaments\/[^/]+\/?$/.test(location.pathname);
  const selectedLeague = useAppSelector(selectSelectedLeague);
  const canManageDivisions = canManageDivisionsInLeague(selectedLeague);
  /** Normalize so React Query keys match CreateDivisionPage (`""` vs `null`). */
  const tournamentId = sessionStorage.getItem("tournamentId") ?? "";

  const [selectedDivisionId, setSelectedDivisionId] = useState<string>("all");
  const [selectedGroupId, setSelectedGroupId] = useState<string>("all");
  const [showSetDivisionsModal, setShowSetDivisionsModal] = useState(false);
  const [updateTeamModal, setUpdateTeamModal] = useState<{
    open: boolean;
    teamId: string;
    teamName: string;
    teamSnapshotId: string;
  }>({ open: false, teamId: "", teamName: "", teamSnapshotId: "" });

  const { data: allTeams = [] } = useQuery<ITeams[]>({
    queryKey: [QUERY_KEY.getAllTeam, isTournamentDetailsRoute ? tournamentId : selectedLeague?.leagueId],
    queryFn: async () => {
      const url = isTournamentDetailsRoute ? `${QUERY_KEY.getAllTeam}/${tournamentId}` : `${QUERY_KEY.getAllTeam}/${selectedLeague?.leagueId}`;
      const response = await Api.get(url);
      return extractTeamsFromGetAllResponse(response?.data);
    },
    enabled: isTournamentDetailsRoute ? !!tournamentId : !!selectedLeague?.leagueId,
  });

  const { data: divisionsData } = useQuery({
    queryKey: [QUERY_KEY.getDivisions, tournamentId],
    queryFn: async () => {
      if (!tournamentId) return [];
      return fetchDivisions(tournamentId);
    },
    enabled: isTournamentDetailsRoute && !!tournamentId,
  });

  const divisionList = React.useMemo(() => normalizeDivisionList(extractDivisionListFromPayload(divisionsData)), [divisionsData]);

  const selectedDivision = divisionList.find((d) => d.divisionId === selectedDivisionId);
  const groupList: IDivisionResponse[] = selectedDivision?.childDivisions ?? [];

  const divisionFilterOptions: FormDropdownOption[] = useMemo(
    () => [
      { value: "all", label: "All Divisions" },
      ...divisionList.map((div) => ({
        value: div.divisionId,
        label: div.divisionName,
      })),
    ],
    [divisionList],
  );

  const groupFilterOptions: FormDropdownOption[] = useMemo(
    () => [
      { value: "all", label: "All Groups" },
      ...groupList.map((grp) => ({
        value: grp.divisionId,
        label: grp.divisionName,
      })),
    ],
    [groupList],
  );

  /** Read teams directly from a getDivisions node, mapped to ITeams shape. */
  const teamsFromNode = React.useCallback((node: IDivisionResponse): ITeams[] => {
    const raw = getTeamsFromDivisionNode(node);
    const seen = new Set<string>();
    const result: ITeams[] = [];
    raw.forEach((entry) => {
      const e = entry as {
        teamSnapshotId?: string;
        snapshotId?: string;
        teamId?: string;
        teamName?: string;
        teamSnapshotName?: string;
        teamCode?: string | null;
        imageUri?: string | null;
        imageUrl?: string | null;
        teamLogoUrl?: string | null;
        team?: {
          teamId?: string;
          teamName?: string;
          teamCode?: string | null;
          winMatchesCount?: number;
          lossMatchesCount?: number;
          drawMatchesCount?: number;
          imageUri?: string | null;
          imageUrl?: string | null;
        };
      };
      const snap = (e.teamSnapshotId ?? e.snapshotId ?? "").trim();
      const teamId = (e.team?.teamId ?? e.teamId ?? snap).trim();
      const teamName = (e.team?.teamName ?? e.teamName ?? "").trim();
      const teamCode = (e.teamCode ?? e.team?.teamCode ?? "").trim() || undefined;
      if (!teamName || !teamId) return;
      const key = snap || teamId;
      if (seen.has(key)) return;
      seen.add(key);
      // Per backend response: prefer the snapshot-level imageUrl (carries the
      // tournament-specific override). Fall back to team.imageUrl, then to the
      // legacy imageUri / teamLogoUrl variants.
      const imageUri = e.imageUrl ?? e.team?.imageUrl ?? e.imageUri ?? e.team?.imageUri ?? e.teamLogoUrl ?? null;
      result.push({
        teamSnapshotId: snap,
        team: {
          teamId,
          teamName,
          winMatchesCount: e.team?.winMatchesCount ?? 0,
          lossMatchesCount: e.team?.lossMatchesCount ?? 0,
          drawMatchesCount: e.team?.drawMatchesCount ?? 0,
          imageUrl: imageUri,
        },
        imageUrl: imageUri,
        ...(teamCode ? { teamCode } : {}),
      });
    });
    return result;
  }, []);

  const visibleTeams = React.useMemo(() => {
    if (!isTournamentDetailsRoute) return allTeams;

    // Division + specific group selected: read teams from the group node in getDivisions
    if (selectedDivisionId !== "all" && selectedGroupId !== "all") {
      const groupNode = divisionList.flatMap((d) => d.childDivisions ?? []).find((g) => g.divisionId === selectedGroupId);
      if (groupNode) {
        const fromNode = teamsFromNode(groupNode);
        console.log("Teams from group node:", fromNode);
        if (fromNode.length > 0) return fromNode;
      }
      // Fallback: direct divisionId field on teamv2 snapshot
      return allTeams.filter((t) => t.divisionId === selectedGroupId);
    }

    // Division selected, all groups → union all group nodes
    if (selectedDivisionId !== "all" && selectedGroupId === "all") {
      const div = divisionList.find((d) => d.divisionId === selectedDivisionId);
      if (div) {
        const groups = div.childDivisions ?? [];
        if (groups.length > 0) {
          const seen = new Set<string>();
          const result: ITeams[] = [];
          groups.forEach((grp) => {
            teamsFromNode(grp).forEach((t) => {
              const key = t.teamSnapshotId || t.team.teamId;
              if (!seen.has(key)) {
                seen.add(key);
                result.push(t);
              }
            });
          });
          if (result.length > 0) return result;
        }
        // No groups or empty: read from division node itself
        const fromDiv = teamsFromNode(div);
        if (fromDiv.length > 0) return fromDiv;
        // Last fallback: divisionId field on teamv2 snapshots
        return allTeams.filter((t) => t.divisionId === selectedDivisionId);
      }
    }

    // No filter → show all tournament teams
    if (allTeams.length > 0) return allTeams;
    return flattenDivisionTeams(divisionList);
  }, [allTeams, isTournamentDetailsRoute, selectedDivisionId, selectedGroupId, divisionList, teamsFromNode]);

  const navigation = (team: ITeams) => {
    navigate("/tournaments/team-info");
    dispatch(setActiveTab(null));
    sessionStorage.setItem("teamId", team?.team?.teamId);
    sessionStorage.setItem("teamSnapshotId", team?.teamSnapshotId);
    sessionStorage.setItem("tournamentId", tournamentId || "");
    const tc = team?.teamCode?.trim();
    if (tc) sessionStorage.setItem("teamCode", tc);
    else sessionStorage.removeItem("teamCode");
    const lid = selectedLeague?.leagueId?.trim() || sessionStorage.getItem("leagueId")?.trim();
    if (lid) sessionStorage.setItem("leagueId", lid);
  };

  const handleDivisionsCreate = (numDivisions: number, numGroups: number) => {
    const leagueIdForNav = selectedLeague?.leagueId || sessionStorage.getItem("leagueId") || "";
    navigate("/tournaments/create-division", {
      state: {
        mode: "create",
        numDivisions,
        numGroups,
        ...(leagueIdForNav ? { leagueId: leagueIdForNav } : {}),
      },
    });
  };

  const handleEditDivision = async () => {
    if (!tournamentId) {
      toast.error("Tournament not found.");
      return;
    }
    try {
      const payload = await queryClient.fetchQuery({
        queryKey: [QUERY_KEY.getDivisions, tournamentId],
        queryFn: () => fetchDivisions(tournamentId),
      });
      const fresh = normalizeDivisionList(extractDivisionListFromPayload(payload));
      const leagueIdForNav = selectedLeague?.leagueId || sessionStorage.getItem("leagueId") || "";
      navigate("/tournaments/create-division", {
        state: {
          mode: "edit",
          divisions: fresh,
          ...(leagueIdForNav ? { leagueId: leagueIdForNav } : {}),
        },
      });
    } catch {
      toast.error("Could not load divisions. Please try again.");
    }
  };

  return (
    <div className={!isTournamentDetailsRoute ? "main-team-content-wrap" : "tournament-team-wrap"}>
      <div className={!isTournamentDetailsRoute ? "main-middle-content-wrap custom-container" : "tournament-middle-wrap"}>
        <div className="teams-wrapper">
          {!isTournamentDetailsRoute && <BreadCrumb />}
          <div className="team-wrapper-container">
            <div className="teams-header">
              {/* <h1 className="heading">Teams</h1>
              {isTournamentDetailsRoute && (
                <button type="button" className="teams-view-toggle" aria-label="Toggle view">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor"/>
                    <rect x="11" y="1" width="6" height="6" rx="1.5" fill="currentColor"/>
                    <rect x="1" y="11" width="6" height="6" rx="1.5" fill="currentColor"/>
                    <rect x="11" y="11" width="6" height="6" rx="1.5" fill="currentColor"/>
                  </svg>
                </button>
              )} */}

              {isTournamentDetailsRoute && (
                <div className="tournament-teams-controls">
                  <div className="teams-filters">
                    <div className="tournament-filter">
                      <FormDropdown
                        id="teams-filter-division"
                        ariaLabel="Filter by division"
                        value={selectedDivisionId}
                        onChange={(v) => {
                          setSelectedDivisionId(v);
                          setSelectedGroupId("all");
                        }}
                        options={divisionFilterOptions}
                        placeholder="All Divisions"
                      />
                    </div>

                    {groupList.length > 0 && (
                      <div className="tournament-filter">
                        <FormDropdown id="teams-filter-group" ariaLabel="Filter by group" value={selectedGroupId} onChange={setSelectedGroupId} options={groupFilterOptions} placeholder="All Groups" />
                      </div>
                    )}
                  </div>

                  {canManageDivisions && (
                    <div className="teams-action-btns">
                      <button
                        type="button"
                        className="teams-action-btn teams-action-btn--create"
                        onClick={() => {
                          const leagueIdForNav =
                            selectedLeague?.leagueId || sessionStorage.getItem("leagueId") || "";
                          navigate("/tournaments/add-players", {
                            state: { tournamentId, leagueId: leagueIdForNav },
                          });
                        }}
                      >
                        + Add Players
                      </button>
                      <button type="button" className="teams-action-btn teams-action-btn--edit" onClick={handleEditDivision}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                        Edit Division
                      </button>
                      <button type="button" className="teams-action-btn teams-action-btn--create" onClick={() => setShowSetDivisionsModal(true)}>
                        + Create New Division
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="teams-card">
              <div className="teams-grid">
                {visibleTeams.map((team) => (
                  <div className="team-cell-wrap" key={`${team.teamSnapshotId || ""}-${team.team.teamId}`}>
                    <div className="team-cell-top">
                      <div className="team-cell" onClick={() => navigation(team)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && navigation(team)}>
                        <span className="logo">
                          {(() => {
                            console.log("Team data for image:", team);
                            // API response can carry the team logo under several keys depending on
                            // the endpoint (snapshot-level imageUrl, team.imageUrl, legacy imageUri,
                            // teamLogoUrl). Pick the first non-empty match and resolve via CDN.
                            const t = team as ITeams & {
                              imageUrl?: string | null;
                              team?: ITeams["team"] & { imageUrl?: string | null };
                            };
                            const raw = t.imageUrl ?? t.team?.imageUrl ?? t.imageUrl ?? t.team?.imageUrl ?? t.teamLogoUrl ?? null;
                            const src = resolvePublicImageUrl(raw) || Images.card_info_image;
                            return (
                              <img
                                src={src}
                                alt="team-logo"
                                onError={(ev) => {
                                  const img = ev.currentTarget;
                                  if (img.src !== Images.card_info_image) img.src = Images.card_info_image;
                                }}
                              />
                            );
                          })()}
                        </span>
                        <span className="name">{team?.team?.teamName}</span>
                      </div>
                      {isTournamentDetailsRoute && canManageDivisions && (
                        <button
                          type="button"
                          className="team-edit-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setUpdateTeamModal({
                              open: true,
                              teamId: team.team.teamId,
                              teamName: team.team.teamName,
                              teamSnapshotId: team.teamSnapshotId ?? "",
                            });
                          }}
                          title="Edit team"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      )}
                    </div>
                    {isTournamentDetailsRoute && (
                      <div className="team-stats">
                        <span className="stat-badge stat-badge--played">
                          PLAYED <strong>{(team.team.winMatchesCount ?? 0) + (team.team.lossMatchesCount ?? 0) + (team.team.drawMatchesCount ?? 0)}</strong>
                        </span>
                        <span className="stat-badge stat-badge--won">
                          WON <strong>{team.team.winMatchesCount ?? 0}</strong>
                        </span>
                        <span className="stat-badge stat-badge--lost">
                          LOST <strong>{team.team.lossMatchesCount ?? 0}</strong>
                        </span>
                        <span className="stat-badge stat-badge--tie">
                          TIE <strong>{team.team.drawMatchesCount ?? 0}</strong>
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {visibleTeams?.length === 0 && (
              <div className="no-data-found">
                <img src={Images.my_leagues_no} alt="No Teams" />
                <p>No Teams Found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <SetDivisionsGroupModal open={showSetDivisionsModal} onOpenChange={setShowSetDivisionsModal} onCreate={handleDivisionsCreate} />

      <UpdateTeamModal
        open={updateTeamModal.open}
        onOpenChange={(open) => setUpdateTeamModal((prev) => ({ ...prev, open }))}
        teamId={updateTeamModal.teamId}
        teamSnapshotIdFromList={updateTeamModal.teamSnapshotId}
        initialData={{ teamName: updateTeamModal.teamName }}
        onSuccess={() => {
          if (isTournamentDetailsRoute && tournamentId) {
            void queryClient.invalidateQueries({
              queryKey: [QUERY_KEY.getAllTeam, tournamentId],
            });
            void queryClient.invalidateQueries({
              queryKey: [QUERY_KEY.getDivisions, tournamentId],
            });
          }
          if (updateTeamModal.teamId) {
            void queryClient.invalidateQueries({
              queryKey: [QUERY_KEY.teamsSnapShotByTeamId, updateTeamModal.teamId],
            });
          }
        }}
      />
    </div>
  );
};

export default Teams;
